// Album covers: the cover colour with the album name.
import { useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link } from '../router';
import { Dice } from '../icons';
import { albumHref, albumNames, skinFor } from '../albums';
import { namesFor } from '../lib/format';
import { t, useT } from '../i18n';
import { reelThumb, Thumb } from './Thumb';
import { useScript } from '../script';
import type { AlbumCover, Peek, Script, Surprise, SurpriseReason } from '../types';

const peekLine = (p: Peek[], script: Script) => p.map((x) => namesFor(x, script)[0]).join(' · ');

export function MoodTile({ c }: { c: AlbumCover }) {
  const t = useT();
  const [script] = useScript();
  const s = skinFor(c.ref);
  const n = albumNames(c, script);
  return (
    <Link href={albumHref(c.ref)} class="mood" style={`background:${s.bg};color:${s.fg};border:${s.border}`}>
      <span class="t1">{n.primary}</span>
      <span class="n">{t('n.recipes', { n: c.count })}</span>
    </Link>
  );
}

/** A book-shaped album cover. size: "row" (Home, 128×150), "grid" (Albums, 176 tall), "mine" (140×92). */
export function BookCover({ c, size }: { c: AlbumCover; size: 'row' | 'grid' | 'mine' }) {
  const t = useT();
  const [script] = useScript();
  const s = skinFor(c.ref);
  const n = albumNames(c, script);
  if (size === 'mine') {
    return (
      <Link href={albumHref(c.ref)} class="book" style={`flex:none;width:140px;height:92px;justify-content:space-between;padding:12px 14px 12px 18px;background:${s.bg};color:${s.fg};border:${s.border}`}>
        <span class="t1 clamp2" style="font-size:17px">{n.primary}</span>
        <span style="position:relative;font-size:13px;font-weight:700">{t('n.recipes', { n: c.count })}</span>
      </Link>
    );
  }
  const row = size === 'row';
  return (
    <Link href={albumHref(c.ref)} class="book-link" style={row ? 'flex:none;width:128px' : ''}>
      <span class="book" style={`height:${row ? 150 : 176}px;background:${s.bg};color:${s.fg};border:${s.border}`}>
        <span class="t1 clamp3" style={`font-size:${row ? 20 : 19}px`}>{n.primary}</span>
        {!row && c.peek.length > 0 && <span class="peek clamp2">{peekLine(c.peek, script)}</span>}
      </span>
      <span style="font-size:13px;color:var(--muted);padding:0 2px">{t('n.recipes', { n: c.count })}</span>
    </Link>
  );
}

export function reasonText(r: SurpriseReason): string {
  const weeks = (d: number) => Math.round(d / 7);
  switch (r.code) {
    case 'never_tried': return t('reason.never_tried');
    case 'make_again': return (r.days ?? 0) >= 14 ? t('reason.make_again_weeks', { n: weeks(r.days!) }) : t('reason.make_again_days', { n: r.days ?? 0 });
    case 'meal_match': return t(`reason.meal.${r.meal ?? 'dinner'}` as Parameters<typeof t>[0]);
    default: return (r.days ?? 0) >= 14 ? t('reason.not_cooked_weeks', { n: weeks(r.days!) }) : t('reason.not_cooked_days', { n: r.days ?? 0 });
  }
}

/** "Surprise me": a dashed button that becomes a dark card with a pick and the reason for it.
 *  "Another" never repeats within this visit until every candidate was shown. */
export function SurpriseBox({ album, label }: { album?: string; label: string }) {
  const t = useT();
  const [script] = useScript();
  const [pick, setPick] = useState<Surprise | null>(null);
  const [seen, setSeen] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const next = async () => {
    setBusy(true);
    setMsg('');
    try {
      const s = await api.surprise(album ?? '', seen);
      setPick(s);
      setSeen(seen.includes(s.recipe.id) ? [s.recipe.id] : [...seen, s.recipe.id]);
    } catch (e) {
      const ae = e as ApiError;
      setMsg(ae.status === 404 ? t('surprise.none') : ae.message);
    } finally {
      setBusy(false);
    }
  };
  if (!pick) {
    return (
      <div style="display:flex;flex-direction:column;gap:8px">
        <button class="surprise-btn" onClick={next} disabled={busy} aria-busy={busy}><Dice />{label}</button>
        {msg && <div class="small muted" role="status" style="text-align:center">{msg}</div>}
      </div>
    );
  }
  const r = pick.recipe;
  const [primary, secondary] = namesFor({ ta: r.dish_name_ta, en: r.dish_name_en, tg: r.dish_name_translit }, script);
  return (
    <div class="surprise-card pop" role="status" aria-live="polite" key={r.id}>
      <div style="display:flex;gap:12px;align-items:center">
        <Thumb src={reelThumb(r.id, r.has_thumb)} name={primary} size="row" />
        <div style="flex:1;min-width:0;display:flex;flex-direction:column">
          <div class="kicker">{t('home.howAbout')}</div>
          <div class="dish" lang={script === 'ta' ? 'ta' : undefined}>{primary}</div>
        </div>
      </div>
      <div class="why">{[secondary, reasonText(pick.reason)].filter(Boolean).join(' · ')}</div>
      <div class="acts">
        <Link href={`/r/${r.id}`} class="go">{t('home.cookThis')}</Link>
        <button class="another" onClick={next} disabled={busy}>{t('home.another')}</button>
      </div>
    </div>
  );
}
